import React, { useEffect, useRef, useState } from 'react';
import { athletesAPI, notificationsAPI, stravaAPI } from '../../api';
import { useTranslation } from '../../i18n';
import { toast } from '../common/Notifier';
import { TELEGRAM_BOT_USERNAME } from '../../constants/bot';
import { formatLastSynced } from '../../utils/formatLastSynced';
import { field, primaryBtn, sectionClass } from './styles';

export const ConnectionsTab = ({ athlete, checkAuth, highlightedId }) => {
  const { t } = useTranslation();

  // --- Strava ---
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaInfo, setStravaInfo] = useState(null);

  // --- Telegram ---
  const telegramLinked = Boolean(athlete?.telegram_id);
  const pollRef = useRef(null);

  // --- Уведомления ---
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd] = useState('07:00');
  const [reminderInterval, setReminderInterval] = useState(2);
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    stravaAPI
      .status()
      .then(({ data }) => {
        setStravaConnected(data.connected);
        if (data.connected) {
          setStravaInfo({
            firstname: data.firstname,
            profile_photo: data.profile_photo,
            last_synced_at: data.last_synced_at,
          });
        }
      })
      .catch(() => setStravaConnected(false));
  }, []);

  // не оставляем опрос Telegram висеть после ухода со страницы
  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleConnectTelegram = async () => {
    try {
      const { data } = await athletesAPI.getTelegramLinkToken();
      window.open(`https://t.me/${TELEGRAM_BOT_USERNAME}?start=${data.token}`, '_blank');

      clearInterval(pollRef.current);
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const updated = await checkAuth();
          if (updated?.telegram_id || attempts >= 30) clearInterval(pollRef.current);
        } catch {
          clearInterval(pollRef.current);
        }
      }, 2000);
    } catch {
      toast.error(t('preferences.telegram_link_error'));
    }
  };

  const handleDisconnectTelegram = async () => {
    try {
      await athletesAPI.disconnectTelegram();
      toast.success(t('preferences.telegram_disconnected'));
      await checkAuth();
    } catch {
      toast.error(t('preferences.telegram_disconnect_error'));
    }
  };

  const handleDisconnectStrava = async () => {
    try {
      await stravaAPI.disconnect();
      setStravaConnected(false);
      setStravaInfo(null);
      toast.success(t('preferences.strava_disconnected'));
    } catch {
      toast.error(t('error.general'));
    }
  };

  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSavingNotif(true);
    try {
      await notificationsAPI.save({
        athlete_id: athlete.id,
        enabled: notifEnabled,
        quiet_hours_start: quietStart,
        quiet_hours_end: quietEnd,
        reminder_interval_hours: reminderInterval,
      });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingNotif(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Ряд 1: Strava | Telegram */}
      <div id="strava-section" className={sectionClass('strava-section', highlightedId, 'gap-3')}>
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <img src="/strava.png" alt="Strava" className="w-5 h-5" />
          {t('preferences.strava_title')}
        </h3>

        {stravaConnected ? (
          <>
            <div className="flex items-center gap-3">
              {stravaInfo?.profile_photo && (
                <img src={stravaInfo.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
              )}
              <div>
                <p className="text-sm text-green-600">
                  ✅ {t('preferences.strava_connected')}
                  {stravaInfo?.firstname ? ` — ${stravaInfo.firstname}` : ''}
                </p>
                {stravaInfo?.last_synced_at && (
                  <p className="text-xs text-gray-400">
                    {t('preferences.strava_last_synced')}: {formatLastSynced(stravaInfo.last_synced_at, t)}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleDisconnectStrava}
              className="mt-auto self-start bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              {t('preferences.strava_disconnect_button')}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">{t('preferences.strava_desc')}</p>
            <a
              href={stravaAPI.getAuthUrl()}
              className="mt-auto self-start bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              {t('preferences.strava_connect_button')}
            </a>
          </>
        )}
      </div>

      <div id="telegram-section" className={sectionClass('telegram-section', highlightedId, 'gap-3')}>
        <h3 className="text-lg font-semibold text-gray-800">🤖 {t('preferences.telegram_title')}</h3>
        {telegramLinked ? (
          <>
            <p className="text-sm text-green-600">✅ {t('preferences.telegram_connected')}</p>
            <button
              onClick={handleDisconnectTelegram}
              className="mt-auto self-start bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              {t('preferences.telegram_disconnect_button')}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">{t('preferences.telegram_desc')}</p>
            <button
              onClick={handleConnectTelegram}
              className="mt-auto self-start bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              {t('preferences.telegram_connect_button')}
            </button>
          </>
        )}
      </div>

      {/* Ряд 2: уведомления на всю ширину */}
      <form
        id="notifications-section"
        onSubmit={handleSaveNotifications}
        className={`${sectionClass('notifications-section', highlightedId)} md:col-span-2`}
      >
        <h3 className="text-lg font-semibold text-gray-800">🔔 {t('preferences.notifications_title')}</h3>

        <label className="flex items-center gap-2 self-start">
          <input
            type="checkbox"
            checked={notifEnabled}
            onChange={(e) => setNotifEnabled(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm text-gray-700">{t('preferences.notifications_enabled')}</span>
        </label>

        {notifEnabled && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('preferences.quiet_from')}</label>
              <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} className={field} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('preferences.quiet_to')}</label>
              <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} className={field} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('preferences.reminder_interval')}</label>
              <select
                value={reminderInterval}
                onChange={(e) => setReminderInterval(parseInt(e.target.value, 10))}
                className={field}
              >
                {[1, 2, 3, 4, 6].map((h) => (
                  <option key={h} value={h}>{t('preferences.every_n_hours', { n: h })}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <button type="submit" disabled={savingNotif} className={`${primaryBtn} md:w-auto md:self-start md:px-8`}>
          {savingNotif ? t('common.loading') : t('preferences.save')}
        </button>
      </form>
    </div>
  );
};